import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Mail, Phone, CheckCircle, FileText, ShieldCheck, XCircle,
  ExternalLink, Loader2, PenLine, AlertTriangle, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import LandingPage from '@/pages/LandingPage';
import Wordmark from '@/components/Wordmark';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIGN_STEPS = ['Gennemlæs', 'SMS-underskrift', 'Underskriv'];
// Begge metoder har samme form: gennemlæs, bevis hvem du er, underskriv.
// MitID beviser hvem personen ER; den afsendte SMS beviser rådighed over det
// nummer, afsenderen har udpeget.
const MITID_STEPS = ['Gennemlæs', 'MitID', 'Underskriv'];

// Interval for polling af SMS-status (ms)
const POLL_INTERVAL_MS = 3000;

const inputClass = "pl-10 h-12 bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted";

// ─── Hjælpere ────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (bytes === null || bytes === undefined || isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('da-DK', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

// Grøn attest-linje ("E-mail godkendt" / "SMS godkendt")
function Attestation({ label, timestamp, detail }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-green-600/30 bg-green-600/10 px-4 py-3">
      <CheckCircle size={20} className="text-green-500 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-th-text font-medium">{label}</p>
        {timestamp && <p className="text-xs text-th-text-muted mt-0.5">{formatDateTime(timestamp)}</p>}
        {detail && <p className="text-xs text-th-text-faint mt-0.5 truncate">{detail}</p>}
      </div>
    </div>
  );
}

// PDF-visning — spejler PdfViewer i DashboardPage
// Fremviseren faar ingen fast hoejde og ingen andel af skaermen. Den fylder
// praecis det, kortet har tilbage, naar resten er tegnet — derfor h-full og
// flex-1 hele vejen ned. En fast hoejde (fx 700px) passer kun paa den skaerm,
// den blev valgt paa; en vh-andel tager ikke hoejde for, hvor meget plads
// overskrift, trinbjaelke og knap allerede har brugt.
function SignPdfViewer({ url, filename, onLoad }) {
  return (
    <div className="rounded-lg border border-th-border-light overflow-hidden flex flex-col h-full min-h-[200px]">
      {/* Mobil: fast 360px bredde centreret. Desktop: fuld bredde */}
      <div className="sm:hidden flex-1 min-h-0 flex justify-center bg-gray-100 dark:bg-gray-900 overflow-hidden">
        <iframe
          src={url}
          title={filename || 'Dokument'}
          onLoad={onLoad}
          className="border-0 h-full"
          style={{ width: '360px' }}
        />
      </div>
      <iframe
        src={url}
        title={filename || 'Dokument'}
        onLoad={onLoad}
        className="hidden sm:block flex-1 min-h-0 w-full border-0"
      />
      <div className="shrink-0 bg-th-hover border-t border-th-border-light px-3 py-2 flex items-center justify-between gap-3">
        <span className="text-xs text-th-text-muted truncate">{filename}</span>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 inline-flex items-center gap-1.5 bg-btn hover:bg-btn-hover text-btn-fg px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
        >
          <ExternalLink size={14} />
          Åbn i nyt vindue
        </a>
      </div>
    </div>
  );
}

// Telefonens beskedapp aabnes med nummer og tekst forudfyldt. macOS Beskeder
// vil have "sms:NUMMER&body=" UDEN spoergsmaalstegn, mens iOS og Android bruger
// "sms:NUMMER?&body=". Rammer man forkert, aabner appen tom.
function erMac() {
  const ua = navigator.userAgent || '';
  return /Macintosh/.test(ua) && !/Mobile/.test(ua);
}

function smsLink(til, tekst) {
  const enc = encodeURIComponent(tekst || '');
  return erMac() ? `sms:${til}&body=${enc}` : `sms:${til}?&body=${enc}`;
}

function erMobil() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

// ─── Side ────────────────────────────────────────────────────────────────────

function SignPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const emailToken = searchParams.get('e');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [step, setStep] = useState(0);
  const [stepLoading, setStepLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [declined, setDeclined] = useState(false);

  // Trin 0 — gennemlæsning
  const [opened, setOpened] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [confirmedRead, setConfirmedRead] = useState(false);
  const openedAtRef = useRef(null);
  const openedPostedRef = useRef(false);

  // Trin 1 — mobilnummer

  // Trin 2 — SMS

  // Afvisning
  // SMS-underskrift: sessionen hentes FOER brugeren klikker. Browsere blokerer
  // en sms:-navigation, der sker efter et async kald — linket skal ligge klar i
  // selve klik-gesten, ellers sker der ingenting paa mobilen.
  const [smsSession, setSmsSession] = useState(null);
  const [smsSessionFejl, setSmsSessionFejl] = useState('');

  // Popup'en kan lukkes, naar der ikke er mere at gore — underskrevet, afvist
  // eller et link der ikke kan aabnes. Midt i flowet lukker den IKKE ved et
  // klik ved siden af: dér ville et forbiklik rive skaermen vaek under en
  // person, der er i gang.
  const [lukket, setLukket] = useState(false);

  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  // Kvitteringens "Luk siden". Browseren afviser window.close() paa et vindue,
  // brugeren selv åbnede (fx via mail-linket) — og gør det tavst. Derfor
  // kontrolleres bagefter, om vinduet stadig er der, og brugeren får besked.
  const [kanIkkeLukke, setKanIkkeLukke] = useState(false);

  const lukSiden = () => {
    window.close();
    setTimeout(() => { if (!window.closed) setKanIkkeLukke(true); }, 300);
  };

  // Afsenderens egen landingsside. Serveren har valideret adressen (kun https);
  // her udledes alene værtsnavnet til knapteksten, så underskriveren kan se,
  // hvor han sendes hen, før han klikker.
  const returUrl = info?.retur_url || null;
  const returVaert = (() => {
    try { return new URL(returUrl).hostname.replace(/^www\./, ''); } catch { return null; }
  })();

  const fileUrl = token ? `${API}/sign/${token}/file` : null;
  const kraevMitid = !!info?.kraev_mitid;
  const trin = kraevMitid ? MITID_STEPS : SIGN_STEPS;
  const underskrivTrin = 2;   // begge metoder: gennemlæs → bevis → underskriv

  // MitID sender brugeren retur hertil med ?mitid=… Beskeden vises én gang,
  // og parameteren fjernes fra adressen, så et genindlæs ikke gentager den.
  useEffect(() => {
    const svar = searchParams.get('mitid');
    if (!svar) return;
    if (svar === 'ok') toast.success('MitID godkendt');
    else if (svar === 'navn') toast.error('Navnet i MitID er ikke det samme som på dokumentet. Kontakt afsenderen.', { duration: 9000 });
    else if (svar === 'afbrudt') toast('MitID blev afbrudt', { icon: '↩︎' });
    else toast.error('MitID kunne ikke gennemføres — prøv igen');
    const rent = new URL(window.location.href);
    rent.searchParams.delete('mitid');
    window.history.replaceState({}, '', rent.toString());
  }, [searchParams]);

  useEffect(() => {
    if (kraevMitid || step !== 1 || smsSession || !token || info?.sms_verified) return;
    let afbrudt = false;
    axios.post(`${API}/sign/${token}/sms-session`, {}, { params: { e: emailToken } })
      .then((r) => { if (!afbrudt && r.data?.id) setSmsSession(r.data); })
      .catch((e) => {
        if (!afbrudt) setSmsSessionFejl(e.response?.data?.detail || 'Kunne ikke starte SMS-underskrift');
      });
    return () => { afbrudt = true; };
  }, [step, kraevMitid, smsSession, token, emailToken, info?.sms_verified]);

  // Udled korrekt trin ud fra serverens statusflag (resume)
  const stepFromStatus = useCallback((d) => {
    if (d?.kraev_mitid) {
      if (d?.mitid_verified) return 2;
      return d?.read ? 1 : 0;
    }
    if (d?.sms_verified) return 2;
    if (d?.read) return 1;
    return 0;
  }, []);

  // ── Indlæs status. Selve kaldet markerer e-mailen som bekræftet server-side ──
  // API'et svarer {document:{…}, signer:{…}, sender_name, …}. Resten af siden
  // laeser felterne fladt, saa vi normaliserer ét sted her.
  const flatten = (raw) => {
    const d = raw || {};
    return {
      ...(d.document || {}),
      ...(d.signer || {}),
      sender_name: d.sender_name,
      min_read_seconds: d.min_read_seconds,
      access_closed: d.access_closed,
      cancelled: (d.document || {}).status === 'cancelled',
      declined: !!(d.signer || {}).declined_at,
    };
  };

  useEffect(() => {
    if (!token) { setError('Manglende link — intet underskrifts-token fundet'); setLoading(false); return; }
    if (!emailToken) { setError('Ugyldigt link — brug linket fra den e-mail du har modtaget'); setLoading(false); return; }

    let cancelled = false;
    axios.get(`${API}/sign/${token}`, { params: { e: emailToken } })
      .then(r => {
        if (cancelled) return;
        const d = flatten(r.data);
        setInfo(d);
        if (d.signed) { setDone(true); return; }
        if (d.declined) { setDeclined(true); return; }
        if (d.cancelled) { setError('Dokumentet er annulleret af afsenderen og kan ikke længere underskrives.'); return; }
        setStep(stepFromStatus(d));
        if (d.read) { setOpened(true); setConfirmedRead(true); }
      })
      .catch(e => {
        if (cancelled) return;
        setError(e.response?.data?.detail || 'Linket er ugyldigt eller udløbet');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [token, emailToken, stepFromStatus]);

  useEffect(() => {
    const paaTast = (e) => {
      if (e.key === 'Escape' && (done || declined || error)) setLukket(true);
    };
    window.addEventListener('keydown', paaTast);
    return () => window.removeEventListener('keydown', paaTast);
  }, [done, declined, error]);

  // ── Trin 0: tæl læsetid ──
  useEffect(() => {
    if (step !== 0 || !opened || confirmedRead) return;
    const id = setInterval(() => {
      if (openedAtRef.current) {
        setElapsed(Math.floor((Date.now() - openedAtRef.current) / 1000));
      }
    }, 500);
    return () => clearInterval(id);
  }, [step, opened, confirmedRead]);

  // ── Trin 1: poll indtil underskriverens SMS er naaet frem ──
  // Trinnummeret SKAL foelge SMS-trinnet. Stod her et forkert tal, ville siden
  // blive staaende paa "Venter paa din SMS", selv om serveren for laengst havde
  // godkendt den — beskeden kom frem, men ingen spurgte efter svaret.
  useEffect(() => {
    if (step !== 1 || kraevMitid || done || declined) return;
    let stopped = false;

    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/sign/${token}`, { params: { e: emailToken } });
        if (stopped) return;
        const d = flatten(r.data);
        setInfo(d);
        if (d.signed) { setDone(true); return; }
        if (d.declined) { setDeclined(true); return; }
        if (d.sms_verified) {
          toast.success('SMS godkendt');
          setStep(underskrivTrin);
        }
      } catch {
        // Ignorer midlertidige netværksfejl under polling — næste tick prøver igen
      }
    }, POLL_INTERVAL_MS);

    return () => { stopped = true; clearInterval(id); };
  }, [step, kraevMitid, token, emailToken, done, declined, underskrivTrin]);

  // ── Handlinger ──

  const handleIframeLoad = () => {
    if (openedPostedRef.current) return;
    openedPostedRef.current = true;
    openedAtRef.current = Date.now();
    setOpened(true);
    axios.post(`${API}/sign/${token}/opened`, {}, { params: { e: emailToken } })
      .catch(() => { /* Ikke-kritisk — læsetiden registreres alligevel ved "Næste" */ });
  };

  const canNext = () => {
    if (stepLoading) return false;
    if (step === 0) return confirmedRead;
    return false;
  };

  const submitRead = async () => {
    setStepLoading(true);
    try {
      await axios.post(`${API}/sign/${token}/read`, { seconds: elapsed }, { params: { e: emailToken } });
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fejl');
    } finally { setStepLoading(false); }
  };

  const submitComplete = async () => {
    setStepLoading(true);
    try {
      await axios.post(`${API}/sign/${token}/complete`, {}, { params: { e: emailToken } });
      // /complete svarer kun {ok, all_signed} — hent status igen, saa
      // kvitteringen viser de rigtige tidsstempler.
      try {
        const fresh = await axios.get(`${API}/sign/${token}`, { params: { e: emailToken } });
        setInfo(flatten(fresh.data));
      } catch {
        // Underskriften er gennemfoert uanset — vis kvitteringen med det vi har
      }
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke underskrive');
    } finally { setStepLoading(false); }
  };

  const submitDecline = async () => {
    setStepLoading(true);
    try {
      await axios.post(`${API}/sign/${token}/decline`, { reason: declineReason }, { params: { e: emailToken } });
      setShowDecline(false);
      setDeclined(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke afvise dokumentet');
    } finally { setStepLoading(false); }
  };

  const next = () => {
    if (step === 0) return submitRead();
  };

  // ── Delvisninger ──

  const declineBlock = (
    <div className="mt-4 pt-3 border-t border-th-border-light">
      {!showDecline ? (
        <button
          type="button"
          onClick={() => setShowDecline(true)}
          className="text-sm text-th-text-muted hover:text-red-400 transition-colors"
        >
          Afvis dokument
        </button>
      ) : (
        <div className="space-y-3 rounded-xl border border-th-border bg-th-hover p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-th-text-secondary">
              Er du sikker på, at du vil afvise dokumentet? Afsenderen får besked. Handlingen kan ikke fortrydes.
            </p>
          </div>
          <Textarea
            value={declineReason}
            onChange={e => setDeclineReason(e.target.value)}
            placeholder="Begrundelse (valgfri)"
            maxLength={500}
            className="bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted min-h-[80px]"
          />
          <div className="flex gap-2">
            <Button
              onClick={submitDecline}
              disabled={stepLoading}
              className="flex-1 h-11 bg-red-600 hover:bg-red-500 text-white"
            >
              {stepLoading ? 'Vent...' : 'Ja, afvis dokumentet'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => { setShowDecline(false); setDeclineReason(''); }}
              disabled={stepLoading}
              className="h-11 text-th-text-secondary hover:text-th-text hover:bg-th-hover"
            >
              Fortryd
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const documentHeader = info && (
    <div className="rounded-xl border border-th-border bg-th-hover px-4 py-2.5">
      <div className="flex items-start gap-3">
        <FileText size={20} className="text-brand-500 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-th-text font-medium break-words">{info.title || info.filename || 'Dokument'}</p>
          <p className="text-xs text-th-text-muted mt-0.5 break-all">
            {info.filename}{info.size ? ` · ${formatBytes(info.size)}` : ''}
          </p>
          {(info.sender_name || info.owner_name || info.sender_email) && (
            <p className="text-xs text-th-text-muted mt-1">
              Sendt af <strong className="text-th-text-secondary">{info.sender_name || info.owner_name || info.sender_email}</strong>
            </p>
          )}
        </div>
      </div>
    </div>
  );

  // Er der intet tilbage at gore, maa popup'en kunne lukkes. Alt er gemt paa
  // serveren, saa siden kan aabnes igen paa samme link.
  const kanLukkes = done || declined || !!error;

  if (lukket) {
    return <LandingPage />;
  }

  return (
    <div className="relative min-h-screen">
      <div className="fixed inset-0 overflow-auto blur-sm brightness-50 pointer-events-none">
        <LandingPage />
      </div>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="absolute inset-0 bg-[var(--surface-overlay)]" />
        <div
          className="relative z-10 min-h-full flex items-center justify-center p-4 py-6"
          onClick={kanLukkes ? (e) => { if (e.target === e.currentTarget) setLukket(true); } : undefined}
        >
          <Card className="w-full max-w-3xl max-h-[94vh] flex flex-col p-4 sm:p-6 bg-th-sidebar border-th-border shadow-2xl shadow-brand-500/10">
            <div className="shrink-0 flex justify-center mb-3 sm:mb-4">
              <a href="/" className="flex items-center gap-2">
                <Mail size={28} className="text-brand-500" />
                <span className="text-xl font-heading font-bold text-th-text">
                  <Wordmark />
                </span>
              </a>
            </div>

            {loading ? (
              <p className="text-th-text-secondary text-center">Indlæser dokument...</p>
            ) : error ? (
              /* ── Fejl / ugyldigt / annulleret ── */
              <div className="text-center space-y-4 max-w-md mx-auto">
                <XCircle size={48} className="text-red-500 mx-auto" />
                <h1 className="text-2xl font-heading font-bold text-th-text">Kan ikke åbnes</h1>
                <p className="text-red-400">{error}</p>
                <p className="text-sm text-th-text-muted">
                  Kontakt afsenderen, hvis du mener det er en fejl.
                </p>
              </div>
            ) : done ? (
              /* ── Underskrevet ── */
              <div className="text-center space-y-5 max-w-lg mx-auto">
                <CheckCircle size={56} className="text-green-500 mx-auto" />
                <h1 className="text-2xl font-heading font-bold text-th-text">Dokumentet er underskrevet</h1>
                <p className="text-th-text-secondary">
                  Tak. Din underskrift af <strong>{info?.title || info?.filename || 'dokumentet'}</strong> er registreret
                  {info?.signed_at ? ` ${formatDateTime(info.signed_at)}` : ''}.
                </p>
                {/* Kvitteringen skal vise det bevis, der faktisk blev brugt — ikke
                    en tom SMS-linje på et dokument, der krævede MitID. Samme
                    regel som attesten og selve underskriftstrinnet. */}
                <div className="space-y-2 text-left">
                  <Attestation
                    label="E-mail godkendt"
                    timestamp={info?.email_verified_at}
                    detail={info?.email}
                  />
                  {kraevMitid ? (
                    <Attestation
                      label="MitID godkendt"
                      timestamp={info?.mitid_verified_at}
                      detail={info?.mitid_name}
                    />
                  ) : (
                    <Attestation
                      label="SMS godkendt"
                      timestamp={info?.sms_verified_at}
                      detail={info?.phone_masked || info?.phone}
                    />
                  )}
                </div>
                <p className="text-sm text-th-text-muted">
                  Der er sendt en kvittering med attest til din e-mail.
                </p>
                {returUrl ? (
                  <Button
                    onClick={() => { window.location.href = returUrl; }}
                    className="w-full h-12 bg-btn hover:bg-btn-hover text-btn-fg"
                  >
                    <span className="inline-flex items-center gap-2">
                      {returVaert ? `Fortsæt til ${returVaert}` : 'Fortsæt'}
                      <ExternalLink size={18} />
                    </span>
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={lukSiden}
                      className="w-full h-12 bg-btn hover:bg-btn-hover text-btn-fg"
                    >
                      Luk siden
                    </Button>
                    {kanIkkeLukke && (
                      <p className="text-sm text-th-text-muted">
                        Browseren tillader kun, at en side lukker sig selv, hvis den blev
                        åbnet af en anden side. Luk fanebladet manuelt.
                      </p>
                    )}
                  </>
                )}
                <p className="text-xs text-th-text-faint border-t border-th-border-light pt-4">
                  Linket giver ikke længere adgang til dokumentet. Har du brug for en kopi,
                  skal du kontakte afsenderen.
                </p>
              </div>
            ) : declined ? (
              /* ── Afvist ── */
              <div className="text-center space-y-4 max-w-md mx-auto">
                <XCircle size={56} className="text-red-500 mx-auto" />
                <h1 className="text-2xl font-heading font-bold text-th-text">Dokumentet er afvist</h1>
                <p className="text-th-text-secondary">
                  Du har afvist at underskrive <strong>{info?.title || info?.filename || 'dokumentet'}</strong>.
                  Afsenderen har fået besked.
                </p>
                {(info?.decline_reason || declineReason) && (
                  <p className="text-sm text-th-text-muted">
                    Begrundelse: {info?.decline_reason || declineReason}
                  </p>
                )}
              </div>
            ) : (
              /* ── Flow ── */
              <>
                {/* Fast top. Underoverskriften og e-mail-linjen er skjult paa
                    lave skaerme — de oplyser, men de maa ikke aede den plads,
                    dokumentet skal laeses i. */}
                <div className="shrink-0">
                  <h1 className="text-xl sm:text-2xl font-heading font-bold text-th-text text-center mb-1">
                    Underskriv dokument
                  </h1>
                  <p className="hidden [@media(min-height:700px)]:block text-th-text-secondary text-center text-sm mb-3">
                    {info?.name ? <><strong>{info.name}</strong> — </> : null}
                    bekræft din identitet og underskriv
                  </p>

                  {/* E-mail bekræftet — sat ved indlæsning af siden */}
                  <div className="hidden [@media(min-height:640px)]:flex items-center justify-center gap-2 mb-3 text-sm text-green-500">
                    <CheckCircle size={16} />
                    <span>E-mail bekræftet{info?.email ? ` (${info.email})` : ''}</span>
                  </div>

                  <div className="flex items-center gap-2 mb-1.5 mt-2">
                    {trin.map((_, i) => (
                      <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-brand-500' : 'bg-th-hover'}`} />
                    ))}
                  </div>
                  <p className="text-xs text-th-text-muted uppercase tracking-wider mb-3">
                    Trin {step + 1} af {trin.length} — {trin[step]}
                  </p>
                </div>

                {/* Elastisk midte. Trin 0 lader fremviseren vokse i stedet for
                    at scrolle; de oevrige trin er korte og scroller, hvis der
                    er brug for det. */}
                <div className={step === 0 ? 'flex-1 min-h-0 flex flex-col' : 'flex-1 min-h-0 overflow-y-auto'}>

                {/* ── Trin 0 — gennemlæs ── */}
                {step === 0 && (
                  <div className="flex flex-col h-full min-h-0 gap-3">
                    <div className="shrink-0">{documentHeader}</div>

                    {fileUrl && (
                      <div className="flex-1 min-h-0">
                        <SignPdfViewer url={fileUrl} filename={info?.filename} onLoad={handleIframeLoad} />
                      </div>
                    )}

                    <label className="shrink-0 flex items-start gap-3 cursor-pointer rounded-xl border border-th-border bg-th-hover px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={confirmedRead}
                        onChange={e => setConfirmedRead(e.target.checked)}
                        className="mt-0.5 h-4 w-4 accent-brand-600 cursor-pointer"
                      />
                      <span className="text-sm text-th-text">
                        Jeg bekræfter at jeg har gennemlæst dokumentet
                      </span>
                    </label>

                  </div>
                )}

                {/* ── Trin 1 — MitID (kun når dokumentet kræver det) ── */}
                {step === 1 && kraevMitid && (
                  <div className="space-y-4 max-w-md mx-auto">
                    <div className="rounded-xl border border-brand-500/40 bg-brand-500/10 px-4 py-5 text-center">
                      <ShieldCheck size={28} className="text-brand-400 mx-auto mb-2" />
                      <p className="text-lg text-th-text font-heading font-bold">Bekræft din identitet med MitID</p>
                      <p className="text-sm text-th-text-secondary mt-2">
                        Afsenderen har krævet MitID på dette dokument. Navnet fra MitID skal
                        være det samme som på dokumentet — ellers kan underskriften ikke gennemføres.
                      </p>
                    </div>
                    <Button
                      onClick={() => { window.location.href = `${API}/sign/${token}/mitid/start?e=${encodeURIComponent(emailToken || '')}`; }}
                      className="w-full h-14 bg-btn hover:bg-btn-hover text-btn-fg text-lg"
                    >
                      <span className="inline-flex items-center gap-3">
                        {/* MitID-logoet er blåt; filteret gør det helt hvidt på knappen */}
                        <img
                          src="/mitid-logo.webp"
                          alt=""
                          aria-hidden="true"
                          className="h-5 w-auto"
                          style={{ filter: 'brightness(0) invert(1)' }}
                        />
                        Underskriv med MitID
                      </span>
                    </Button>
                    <p className="text-xs text-th-text-muted text-center">
                      Du sendes til MitID og kommer tilbage hertil bagefter. Vi gemmer dit navn og
                      tidspunktet — ikke dit CPR-nummer.
                    </p>
                  </div>
                )}

                {/* ── Trin 1 — underskriv ved at sende en SMS ── */}
                {step === 1 && !kraevMitid && (
                  /* Samme form som MitID-trinnet: én smal soejle, centreret,
                     og QR-koden staaende for sig selv. */
                  <div className="space-y-4 max-w-md mx-auto">
                    <div className="rounded-xl border border-brand-500/40 bg-brand-500/10 px-4 py-5 text-center">
                      <Phone size={28} className="text-brand-400 mx-auto mb-2" />
                      <p className="text-lg text-th-text font-heading font-bold">
                        Send en SMS fra din telefon
                      </p>
                      {/* Beskeden skal komme fra DET nummer, afsenderen har
                          oplyst — ellers afvises den. Maskeret, saa siden ikke
                          roeber nummeret for enhver, der faar linket. */}
                      {smsSession?.phone_masked && (
                        <p className="text-sm text-th-text-secondary mt-1">
                          Send fra <strong className="text-th-text font-mono">{smsSession.phone_masked}</strong>
                        </p>
                      )}
                    </div>

                    {/* En besked fra et andet nummer bliver afvist. Uden denne
                        besked ville siden bare blive staaende paa "venter",
                        mens vi vidste praecis hvorfor det ikke skete. */}
                    {info?.sms_afvist_at && (
                      <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2">
                        <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-600 dark:text-amber-400">
                          Vi modtog en besked fra <strong className="font-mono">{info.sms_afvist_fra}</strong>.
                          Den skal sendes fra <strong className="font-mono">{smsSession?.phone_masked || 'dit registrerede nummer'}</strong> —
                          prøv igen fra den rigtige telefon.
                        </p>
                      </div>
                    )}

                    {smsSessionFejl && (
                      <div className="flex items-start gap-2 rounded-lg border border-red-600/40 bg-red-600/10 px-3 py-2">
                        <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
                        <p className="text-sm text-red-400">{smsSessionFejl}</p>
                      </div>
                    )}

                    {smsSession && (erMobil() ? (
                      /* Paa telefonen er der ingen grund til en QR-kode —
                         beskedappen er paa selve enheden. Linket ligger klar i
                         knappen, saa browseren ikke blokerer navigationen. */
                      <a
                        href={smsLink(smsSession.sms_to, smsSession.sms_body)}
                        className="flex items-center justify-center gap-2 w-full h-14 rounded-lg bg-btn hover:bg-btn-hover text-btn-fg text-lg font-medium transition-colors"
                      >
                        <MessageSquare size={20} />
                        Åbn beskeder og send
                      </a>
                    ) : (
                      <div className="text-center">
                        <div className="inline-block bg-white p-3 rounded-xl">
                          <QRCodeSVG
                            value={smsLink(smsSession.sms_to, smsSession.sms_body)}
                            size={150}
                            level="M"
                          />
                        </div>
                        <p className="text-sm text-th-text-secondary mt-3">
                          Scan QR-koden med din telefon. Beskeden åbner forudfyldt — tryk Send,
                          og vend tilbage hertil.
                        </p>

                        <div className="mt-4 pt-3 border-t border-th-border-light">
                          <p className="text-[11px] uppercase tracking-wide text-th-text-faint mb-1.5">
                            Eller send fra computeren
                          </p>
                          <div className="flex flex-wrap justify-center gap-1.5">
                            <a href={smsLink(smsSession.sms_to, smsSession.sms_body)}
                              className="text-xs px-2.5 py-1 rounded-md border border-th-border text-th-text-secondary hover:bg-th-hover transition-colors">
                              Apple Beskeder
                            </a>
                            <a href="https://messages.google.com/web" target="_blank" rel="noreferrer"
                              className="text-xs px-2.5 py-1 rounded-md border border-th-border text-th-text-secondary hover:bg-th-hover transition-colors">
                              Google Beskeder
                            </a>
                            <a href="https://www.microsoft.com/phone-link" target="_blank" rel="noreferrer"
                              className="text-xs px-2.5 py-1 rounded-md border border-th-border text-th-text-secondary hover:bg-th-hover transition-colors">
                              Microsoft
                            </a>
                          </div>
                          {/* Ærligt: de to sidste kan ikke forudfylde beskeden
                              — de aabner bare afsenderens egen webapp. */}
                          <p className="text-[11px] text-th-text-faint mt-1.5">
                            Google Beskeder og Microsoft åbner uden forudfyldt tekst. Send til{' '}
                            <span className="font-mono">{smsSession.sms_to}</span> med teksten
                            «ID: {smsSession.id}».
                          </p>
                        </div>
                      </div>
                    ))}

                    {!smsSession && !smsSessionFejl && (
                      <div className="flex items-center justify-center gap-2 py-6">
                        <Loader2 size={18} className="animate-spin text-th-text-muted" />
                        <span className="text-sm text-th-text-muted">Klargør…</span>
                      </div>
                    )}

                    <div className="flex items-center justify-center gap-3">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-500 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-500" />
                      </span>
                      <span className="text-sm text-th-text-secondary animate-pulse">Venter på din SMS…</span>
                    </div>
                  </div>
                )}

                {/* ── Sidste trin — underskriv ── */}
                {step === underskrivTrin && (
                  <div className="space-y-4 max-w-lg mx-auto">
                    {documentHeader}

                    <div className="space-y-2">
                      <Attestation
                        label="E-mail godkendt"
                        timestamp={info?.email_verified_at}
                        detail={info?.email}
                      />
                      {kraevMitid ? (
                        <Attestation
                          label="MitID godkendt"
                          timestamp={info?.mitid_verified_at}
                          detail={info?.mitid_name}
                        />
                      ) : (
                        <Attestation
                          label="SMS godkendt"
                          timestamp={info?.sms_verified_at}
                          detail={info?.phone_masked || info?.phone}
                        />
                      )}
                    </div>

                    <div className="rounded-xl border border-th-border bg-th-hover px-4 py-3">
                      <div className="flex items-start gap-3">
                        <ShieldCheck size={20} className="text-brand-500 shrink-0 mt-0.5" />
                        <p className="text-sm text-th-text-secondary">
                          <strong className="text-red-500">Vigtigt!</strong> — Ved at trykke{' '}
                          <strong className="text-th-text">Underskriv dokument</strong> bekræfter du,
                          at du har gennemlæst dokumentet, at du er den person, der er identificeret ovenfor,
                          og at du underskriver det juridisk bindende. Tidspunkt, IP-adresse og
                          verifikationsoplysninger gemmes i aftalen.
                        </p>
                      </div>
                    </div>

                    <Button
                      onClick={submitComplete}
                      disabled={stepLoading}
                      className="w-full h-14 bg-btn hover:bg-btn-hover text-btn-fg text-lg"
                    >
                      {stepLoading ? 'Underskriver...' : (
                        <span className="inline-flex items-center gap-2">
                          <PenLine size={20} />
                          Underskriv dokument
                        </span>
                      )}
                    </Button>
                  </div>
                )}

                </div>

                {/* Fast bund. Knappen skal kunne ses uden at scrolle — det var
                    hele grunden til, at kortet blev en kolonne. */}
                <div className="shrink-0">
                  {/* Næste-knap — kun efter gennemlæsningen. Derefter er det
                      underskriverens egen SMS eller MitID, der fører videre. */}
                  {step === 0 && (
                    <div className="max-w-md mx-auto">
                      <Button
                        onClick={next}
                        disabled={!canNext()}
                        className="w-full h-12 bg-btn hover:bg-btn-hover text-btn-fg text-lg mt-4"
                      >
                        {stepLoading ? 'Vent...' : 'Næste'}
                      </Button>
                    </div>
                  )}

                  {declineBlock}
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default SignPage;
